import { createRef } from 'react';
import { Select, MenuItem, Box } from '@mui/material';
import Audio1000HzLow from '@resources/audios/tone1000Hz_low_0.01.mp3';
import Audio1000HzMedium from '@resources/audios/tone1000Hz_medium_0.03.mp3';
import Audio1000HzHigh from '@resources/audios/tone1000Hz_high_0.1.mp3';
import HighVolume from '@resources/images/highVolume.svg';
import MediumVolume from '@resources/images/mediumVolume.svg';
import LowVolume from '@resources/images/lowVolume.svg';
import { I18nTranslate } from '@common/i18n';
import { Controller, useFormContext } from 'react-hook-form';
import * as styles from './styles.scss';

export const AUDIO_TONE_LEVEL = {
  LOW: 0.01,
  MEDIUM: 0.03,
  HIGH: 0.1,
} as const;
export type AudioToneLevel =
  (typeof AUDIO_TONE_LEVEL)[keyof typeof AUDIO_TONE_LEVEL];

export const AUDIO_SOURCES = {
  [AUDIO_TONE_LEVEL.LOW]: Audio1000HzLow,
  [AUDIO_TONE_LEVEL.MEDIUM]: Audio1000HzMedium,
  [AUDIO_TONE_LEVEL.HIGH]: Audio1000HzHigh,
} as const;
const SPEAKER_ICONS = {
  [AUDIO_TONE_LEVEL.LOW]: LowVolume,
  [AUDIO_TONE_LEVEL.MEDIUM]: MediumVolume,
  [AUDIO_TONE_LEVEL.HIGH]: HighVolume,
} as const;
export const getAudioSource = (level: number): string => {
  if (level <= 0.01) {
    return AUDIO_SOURCES[AUDIO_TONE_LEVEL.LOW];
  }
  if (level <= 0.03) {
    return AUDIO_SOURCES[AUDIO_TONE_LEVEL.MEDIUM];
  }
  return AUDIO_SOURCES[AUDIO_TONE_LEVEL.HIGH];
};
const style = {
  select: {
    '&.MuiInputBase-root': {
      fontSize: '0.85rem',
    },
  },
  menuItem: {
    '&.MuiMenuItem-root': {
      display: 'flex',
      px: 2,
      py: 0.75,
      justifyContent: 'flex-start',
      fontSize: '0.85rem',
    },
    '&.MuiButtonBase-root-MuiMenuItem-root:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&.MuiMenuItem-root.Mui-selected': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
    },
  },
};

const ToneVolumeSelector = () => {
  const { control, watch } = useFormContext();
  const audioRef = createRef<HTMLAudioElement>();

  const audioToneLevel = watch(
    'audioToneLevel',
    AUDIO_TONE_LEVEL.MEDIUM
  ) as AudioToneLevel;

  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.load();
      void audioRef.current.play();
      setTimeout(() => {
        audioRef.current?.pause();
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
        }
      }, 400);
    }
  };
  const speakerIcon = SPEAKER_ICONS[audioToneLevel];
  const volumeLabel =
    audioToneLevel === AUDIO_TONE_LEVEL.LOW
      ? 'Low'
      : audioToneLevel === AUDIO_TONE_LEVEL.MEDIUM
        ? 'Medium'
        : 'High';

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Controller
        name="audioToneLevel"
        control={control}
        render={({ field }) => (
          <Select
            data-testid="tone-volume-selector"
            {...field}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            value={field.value}
            className={styles.selectToneVolume}
            variant={'standard'}
            sx={style.select}
          >
            <MenuItem value={AUDIO_TONE_LEVEL.LOW} sx={style.menuItem}>
              {I18nTranslate.TranslateMessage('low')}
            </MenuItem>
            <MenuItem value={AUDIO_TONE_LEVEL.MEDIUM} sx={style.menuItem}>
              {I18nTranslate.TranslateMessage('medium')}
            </MenuItem>
            <MenuItem value={AUDIO_TONE_LEVEL.HIGH} sx={style.menuItem}>
              {I18nTranslate.TranslateMessage('high')}
            </MenuItem>
          </Select>
        )}
      />

      <img
        data-testid="tone-volume-speaker"
        src={speakerIcon}
        alt={`Speaker ${volumeLabel}`}
        className={styles.iconVolume}
        onClick={playAudio}
        title={`Preview tone volume: ${volumeLabel}`}
      />

      <audio ref={audioRef} key={audioToneLevel}>
        <source src={AUDIO_SOURCES[audioToneLevel]} type="audio/mp3" />
      </audio>
    </Box>
  );
};

export default ToneVolumeSelector;
